/** The pages that can be put beside the one being read: more lines than this cannot be read on one chart. */
export const MAX_ADDED = 3;

export const LIMIT = `Up to ${MAX_ADDED} pages beside this one`;

/** Why `id` cannot be added beside `pageId` with `added` already there; null when it can. */
export const refusalOf = (added, id, pageId) => id === pageId ? "This is the page being read"
    : added.includes(id) ? "Already added"
    : added.length >= MAX_ADDED ? LIMIT
    : null;

/** The pages `?with=` puts beside `pageId`, in order: monitored URIs in the index, each once, up to MAX_ADDED. */
export const addedIdsOf = (params, pageId, indexPages) => params.getAll("with").reduce(
    (added, id) => !refusalOf(added, id, pageId) && indexPages.some((page) => page.id === id) ? [...added, id] : added,
    [],
);

/**
 * What the page's comparison at the chosen start offers before anything is typed, minus this page: one
 * entry per page, in the order first offered, with every reason it is offered (`day` is the rise's).
 */
export const suggestionsOf = (compare, pageId) => {
    if (!compare) return [];
    const { canonicalTarget, redirectDestination, rising } = compare;
    const offers = [
        canonicalTarget && { ...canonicalTarget, label: "Canonical target" },
        redirectDestination && { ...redirectDestination, label: "Redirect destination" },
        ...(rising.sessions || []).map((page) => ({ ...page, label: "Sessions rose" })),
        ...(rising.impressions || []).map((page) => ({ ...page, label: "Impressions rose" })),
    ].filter((offer) => offer && offer.id !== pageId);
    return offers.reduce((pages, { id, path, label, day }) => {
        const known = pages.find((each) => each.id === id);
        const reason = day ? { label, day } : { label };
        return known
            ? pages.map((each) => each === known ? { ...each, reasons: [...each.reasons, reason] } : each)
            : [...pages, { id, path, reasons: [reason] }];
    }, []);
};

/** The words typed, lower-cased: what `matchesOf` looks for. */
export const wordsOf = (text) => text.trim().toLowerCase().split(/\s+/).filter(Boolean);

const MAX_MATCHES = 8;

/** Whether `a` becomes `b` with at most one letter added, dropped or changed. */
const withinOneEdit = (a, b) => {
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    return a.slice(i + (a.length >= b.length ? 1 : 0)) === b.slice(i + (b.length >= a.length ? 1 : 0));
};

/** The typos `word` needs to appear in `path`: 0 when it is there as typed, 1 when one edit away (words of four letters or more), null otherwise. */
const typosOf = (path, word) => {
    if (path.includes(word)) return 0;
    if (word.length < 4) return null;
    for (let at = 0; at < path.length; at++) {
        for (const length of [word.length - 1, word.length, word.length + 1]) {
            if (withinOneEdit(path.slice(at, at + length), word)) return 1;
        }
    }
    return null;
};

/**
 * The pages whose path holds every word typed, in any order and ignoring case, each as typed or one
 * typo away, minus this page: fewest typos first, then the shortest path.
 */
export const matchesOf = (indexPages, text, pageId) => {
    const words = wordsOf(text);
    if (words.length === 0) return [];
    return indexPages
        .filter(({ id }) => id !== pageId)
        .map((page) => {
            const path = page.path.toLowerCase();
            const typos = words.map((word) => typosOf(path, word));
            return typos.includes(null) ? null : { page, typos: typos.reduce((sum, each) => sum + each, 0) };
        })
        .filter(Boolean)
        .sort((a, b) => a.typos - b.typos || a.page.path.length - b.page.path.length)
        .slice(0, MAX_MATCHES)
        .map(({ page }) => page);
};

/** Each added page's colour on the chart, the chips and the table, by its place in `?with=`. */
export const ADDED_COLORS = ["#16a34a", "#9f1239", "#111827"];
